import React from 'react';
import accounting from 'accounting';
import moment from 'moment/moment';
import {ADVANCE_DISBURSEMENTS_TRANSITIONAL_STATUS, CURRENCY, LEGAL_ADVICE_TEXT, POLICYHOLDER_TEXT, UI_LIB_TABLE_VIEW_OPTIONS} from '../../helpers/variables';
import '../../style/advanceDisbursementsList.css'
import { isAragUser, isSolicitorUser, goAtPrevious, goAtNext, goAtStart, goAtEnd, goAtPage } from '../../helpers/functions';
import AdvanceDisbursementsOverviewTable from './advanceDisbursementsOverviewTable';
import { Button, FormGroup, Heading, Loader, Table } from 'arag-ui-lib';
import { DISBURSEMENTS_EXCEL_FILE_DOWNLOAD_BUTTON_INFO, DISBURSEMENTS_EXCEL_FILE_DOWNLOAD_BUTTON_LABEL, PRIMARY, QUATERNARY, TERTIARY, BUTTON, RIGHT, MOMENT_DATE_FORMAT, DOTS, MEDIUM, HEADING_2 } from '../../helpers/constants';
import { FORM_GROUP_VARIANTS } from '../../helpers/formGroupVariants';
import CONTENT_STRINGS from '../../helpers/contentStrings';
import GroupButtons from '../../helpers/groupButtons';
import SolicitorFirmSearchSelect from '../solicitorFirmSearchSelect';

const columns = [
    { propertyName: 'aragReference', header: CONTENT_STRINGS.POLICY_NUMBER },
    { propertyName: 'applicationStatusName', header: CONTENT_STRINGS.POLICY_STATUS },
    { propertyName: 'clientName', header: `${POLICYHOLDER_TEXT} ${CONTENT_STRINGS.NAME}` },
    { propertyName: 'expenseTypeName', header: CONTENT_STRINGS.DISBURSEMENT_TYPE },
    { propertyName: 'amount', header: CONTENT_STRINGS.AMOUNT, formatter: (row) => accounting.formatMoney(row.amount, `${CURRENCY}`, 2) },
    { propertyName: 'disbursementStatusName', header: CONTENT_STRINGS.STATUS, formatter: (ad) => (
        ad.paymentInstructionId && ad.disbursementStatusMachineName === 'approved'
            ? ADVANCE_DISBURSEMENTS_TRANSITIONAL_STATUS
            : ad.disbursementStatusName
    )},
    { propertyName: 'creationDate', header: CONTENT_STRINGS.DATE_CREATED, formatter: (row) => moment(row.creationDate).format(MOMENT_DATE_FORMAT) },
    { propertyName: 'paymentDate', header: CONTENT_STRINGS.DATE_PAID, formatter: (row) => (row.paymentDate ? moment(row.paymentDate).format(MOMENT_DATE_FORMAT) : '') },
    { propertyName: 'paymentInstructionId', header: CONTENT_STRINGS.PAYMENT_REFERENCE, formatter: (row) => (row.paymentInstructionId ? row.paymentInstructionId : '') },
];

const AdvancedDisbursementsList = (props) => {
    const {
        caseTypes,
        fetchingCaseTypes,
        policyStatuses,
        fetchingPolicyStatuses,
        filterByStatusOptions,
        changeStoreSettings,
        loggedInUserRoles,
        subCaseTypes,
        clearFilters,
        totalNumberOfAdvancedDisbursements,
        totalNumberOfApplied,
        totalNumberOfApproved,
        totalValueOfAdvancedDisbursements,
        totalValueOfApplied,
        totalValueOfApproved,
        totalSettledRecords,
        totalSettledValue,
        totalRejectedRecords,
        totalRejectedValue,
        totalProcessingRecords,
        totalProcessingValue,
        totalPaidRecords,
        totalPaidValue,
        fetchingADDueForPayment,
        totalPartSettledRecords,
        totalPartSettledValue,
        advancedDisbursementsList,
        onExportExcel,
        _onAdSuggestionSelected,
        _handleAutosuggestButton,
        _handleInputChange,
        _filterByRange,
        onPrintStatement,
        _changeTableViewOptions,
        _changeAdvancedDisbursementsPage,
        _handleAdSelect,
        fetchingAdvancedDisbursements,
        advancedDisbursements,
    } = props;

    const isArag = isAragUser(loggedInUserRoles);
    const isSolicitor = isSolicitorUser(loggedInUserRoles);

    const tableColumns = isArag
        ? columns
        : columns.filter(col => col.propertyName !== 'applicationStatusName');

    const policyStatusOptions = policyStatuses.map(status => ({ value: status.id, label: status.name, machineName: status.machineName }));
    const caseTypeOptions = caseTypes.map(type => ({ value: type.id, label: type.name, machineName: type.machineName }));

    const showSubCaseTypes = subCaseTypes.length > 0 || advancedDisbursementsList.selectedSubCaseTypes.length > 0;
    // WI: Statement only totals the solicitor's primary firm, omitting linked-firm ADs - disabled for solicitors until multi-firm grouping ships.
    const downloadStatementDisabled = isSolicitor || (isArag && advancedDisbursementsList.selectedFirms.length !== 1);
    const exportExcelDisabled = !advancedDisbursementsList?.selectedFirms?.length;
    const downloadStatementInfo = isSolicitor
        ? CONTENT_STRINGS.DOWNLOAD_STATEMENT_DISABLED_SOLICITOR_TOOLTIP
        : isArag
            ? `${CONTENT_STRINGS.DOWNLOAD_STATEMENT_TOOLTIP_PREFIX} ${LEGAL_ADVICE_TEXT.toLowerCase()} ${CONTENT_STRINGS.DOWNLOAD_STATEMENT_TOOLTIP_SUFFIX}`
            : null;
    const legalAdviceFirmsLabel = `${LEGAL_ADVICE_TEXT} ${CONTENT_STRINGS.FIRMS}`;

    const currentPageNum = parseInt(advancedDisbursementsList.page);
    const totalPagesCount = advancedDisbursementsList.totalPaginationBoxes;
    const handleFirmsChange = (values) => changeStoreSettings({ key: 'selectedFirms', value: values });
    const handlePolicyStatusesChange = (values) => changeStoreSettings({ key: 'selectedPolicyStatuses', value: values });
    const handleCaseTypesChange = (values) => changeStoreSettings({ key: 'selectedCaseTypes', value: values });
    const handleSubCaseTypesChange = (values) => changeStoreSettings({ key: 'selectedSubCaseTypes', value: values });
    const handleStatusesChange = (values) => changeStoreSettings({ key: 'selectedStatuses', value: values });
    const handleFormSubmit = (e) => e.preventDefault();
    const handlePageChange = (page) => _changeAdvancedDisbursementsPage('page', page);
    const handlegoAtPrevious = goAtPrevious(handlePageChange, currentPageNum);
    const handlegoAtNext = goAtNext(handlePageChange, currentPageNum, totalPagesCount);
    const handlegoAtStart = goAtStart(handlePageChange);
    const handlegoAtEnd = goAtEnd(handlePageChange, totalPagesCount);
    const handlegoAtPage = goAtPage(handlePageChange);
    const accordionItemHeaderFormatter = (row) => `${CONTENT_STRINGS.POLICY_NUMBER}: ${row.aragReference} ${CONTENT_STRINGS.DISBURSEMENT_TYPE}: ${row.expenseTypeName}`;
    const handleSearchInputChange = (inputVal, { action }) => {
        if (action === 'input-change') changeStoreSettings({ key: 'textFilter', value: inputVal || '' });
    };
    const handleSearchSelect = (option) => {
        if (!option) return;
        const suggestion = (advancedDisbursementsList.suggestions || []).find((s) => s.applicationId === option.value);
        if (suggestion) _onAdSuggestionSelected(null, { suggestion, method: 'click' });
    };
    const tableActions = [{ label: CONTENT_STRINGS.DETAILS, callback: (row) => _handleAdSelect(row.applicationId) }];
    const searchOptions = (advancedDisbursementsList.suggestions || []).map((s) => ({
        value: s.applicationId,
        label: `${s.aragReference || ''}${s.amount ? ` | ${CURRENCY} ${s.amount}` : ''}`,
    }));

    return (
        <div className={'advance-disbursements-wrapper'}>
            <Heading 
                title={CONTENT_STRINGS.ADVANCE_DISBURSEMENTS}
                variant={HEADING_2}
            />
            {isArag ? (
                <div className={'row'}>
                    <div className={'col-sm-12 mb-15'}>
                        <AdvanceDisbursementsOverviewTable
                            totalNumberOfAdvancedDisbursements={totalNumberOfAdvancedDisbursements}
                            totalNumberOfApplied={totalNumberOfApplied}
                            totalNumberOfApproved={totalNumberOfApproved}
                            totalSettledRecords={totalSettledRecords}
                            totalValueOfAdvancedDisbursements={totalValueOfAdvancedDisbursements}
                            totalValueOfApplied={totalValueOfApplied}
                            totalValueOfApproved={totalValueOfApproved}
                            totalSettledValue={totalSettledValue}
                            totalRejectedRecords={totalRejectedRecords}
                            totalRejectedValue={totalRejectedValue}
                            totalProcessingRecords={totalProcessingRecords}
                            totalProcessingValue={totalProcessingValue}
                            totalPaidRecords={totalPaidRecords}
                            totalPaidValue={totalPaidValue}
                            totalPartSettledRecords={totalPartSettledRecords}
                            totalPartSettledValue={totalPartSettledValue}
                            isAragUser={isArag}
                        />
                    </div>
                </div>
            ) : null}
            <form className='form-horizontal' onSubmit={handleFormSubmit}>
                {isArag ? (
                    <>
                        <SolicitorFirmSearchSelect
                            labelSize={'col-sm-2'}
                            inputSize={'col-sm-7'}
                            label={legalAdviceFirmsLabel}
                            instanceId={'disbursementFirmsMultiSelectInstanceId'}
                            isMulti={true}
                            onChange={handleFirmsChange}
                            value={advancedDisbursementsList.selectedFirms}
                            formGroupTestId={'advance-disbursements-solicitor-firms'}
                        />
                        <FormGroup
                            labelSize={'col-sm-2'}
                            inputSize={'col-sm-7'}
                            label={CONTENT_STRINGS.POLICY_STATUS}
                            variant={FORM_GROUP_VARIANTS.MULTI_SELECT}
                            inputProps={{
                                instanceId: 'disbursementPolicyStatusesMultiSelectInstanceId',
                                isLoading: fetchingPolicyStatuses,
                                placeholder: CONTENT_STRINGS.QUICK_SEARCH,
                                onChange: handlePolicyStatusesChange,
                                value: advancedDisbursementsList.selectedPolicyStatuses,
                                options: policyStatusOptions,
                            }}
                            formGroupTestId={'advance-disbursements-policy-status'}
                        />
                    </>
                ) : null}
                <FormGroup
                    labelSize={'col-sm-2'}
                    inputSize={'col-sm-7'}
                    label={CONTENT_STRINGS.CASE_TYPES}
                    variant={FORM_GROUP_VARIANTS.MULTI_SELECT}
                    inputProps={{
                        instanceId: 'disbursementCaseTypesMultiSelectInstanceId',
                        isLoading: fetchingCaseTypes,
                        placeholder: CONTENT_STRINGS.QUICK_SEARCH,
                        onChange: handleCaseTypesChange,
                        value: advancedDisbursementsList.selectedCaseTypes,
                        options: caseTypeOptions,
                    }}
                    formGroupTestId={'advance-disbursements-case-types'}
                />
                {showSubCaseTypes ? (
                    <FormGroup
                        labelSize={'col-sm-2'}
                        inputSize={'col-sm-7'}
                        label={CONTENT_STRINGS.SUB_CASE_TYPES}
                        variant={FORM_GROUP_VARIANTS.MULTI_SELECT}
                        inputProps={{
                            instanceId: 'disbursementSubCaseTypesMultiSelectInstanceId',
                            isLoading: fetchingCaseTypes,
                            placeholder: CONTENT_STRINGS.QUICK_SEARCH,
                            onChange: handleSubCaseTypesChange,
                            value: advancedDisbursementsList.selectedSubCaseTypes,
                            options: subCaseTypes,
                        }}
                        formGroupTestId={'advance-disbursements-sub-case-types'}
                    />
                ) : null}
                <FormGroup
                    labelSize={'col-sm-2'}
                    inputSize={'col-sm-7'}
                    label={CONTENT_STRINGS.STATUSES}
                    variant={FORM_GROUP_VARIANTS.MULTI_SELECT}
                    inputProps={{
                        instanceId: 'disbursementStatusesMultiSelectInstanceId',
                        isLoading: fetchingCaseTypes,
                        placeholder: CONTENT_STRINGS.QUICK_SEARCH,
                        onChange: handleStatusesChange,
                        value: advancedDisbursementsList.selectedStatuses,
                        options: filterByStatusOptions,
                    }}
                    formGroupTestId={'advance-disbursements-statuses'}
                />
                <FormGroup
                    labelSize={'col-sm-2'}
                    inputSize={'col-sm-7'}
                    label={CONTENT_STRINGS.SEARCH}
                    variant={FORM_GROUP_VARIANTS.SEARCHABLE_LIST}
                    inputProps={{
                        instanceId: 'disbursementSearchSearchableListInstanceId',
                        options: searchOptions,
                        onChange: handleSearchSelect,
                        onInputChange: handleSearchInputChange,
                        inputValue: advancedDisbursementsList.textFilter,
                        value: null,
                        filterOption: () => true,
                        onSearchIconClick: _handleAutosuggestButton,
                        noOptionsText: CONTENT_STRINGS.NO_RESULTS_FOUND,
                        placeholder: CONTENT_STRINGS.QUICK_SEARCH,
                    }}
                    formGroupTestId={'advance-disbursements-search'}
                />
                <FormGroup
                    labelSize={'col-sm-2'}
                    inputSize={'col-sm-3'}
                    label={`${CONTENT_STRINGS.AMOUNT_FROM}: `}
                    variant={FORM_GROUP_VARIANTS.TEXT}
                    inputProps={{
                        type: 'number',
                        name: 'amountFrom',
                        value: advancedDisbursementsList.amountFrom,
                        onChange: _handleInputChange,
                        step: 0.01,
                        min: 0,
                    }}
                    hasInlineElements={true}
                    inlineGroupElements={
                        <div className={'col-sm-4'}>
                            <FormGroup
                                labelSize={'col-sm-2'}
                                inputSize={'col-sm-10'}
                                label={`${CONTENT_STRINGS.TO}: `}
                                variant={FORM_GROUP_VARIANTS.TEXT}
                                inputProps={{
                                    type: 'number',
                                    name: 'amountTo',
                                    value: advancedDisbursementsList.amountTo,
                                    onChange: _handleInputChange,
                                    step: 0.01,
                                    min: 0,
                                }}
                                formGroupTestId={'advance-disbursements-amount-to'}
                            />
                        </div>
                    }
                    formGroupTestId={'advance-disbursements-amount-range'}
                />
                <div className='advance-disbursements-amount-range col-sm-offset-2'>
                    <GroupButtons
                        primaryLabel={CONTENT_STRINGS.FILTER_LABEL}
                        onPrimaryClick={_filterByRange}
                        primaryVariant={PRIMARY}
                        primaryType={BUTTON}
                        primaryTestId={'advance-disbursements-filter'}
                        secondaryLabel={CONTENT_STRINGS.CLEAR_FILTERS_LABEL}
                        onSecondaryClick={clearFilters}
                        secondaryVariant={QUATERNARY}
                        secondaryType={BUTTON}
                        secondaryTestId={'advance-disbursements-clear-filters'}
                        arrowDirection={RIGHT}
                    />
                    <Button
                        disabled={downloadStatementDisabled}
                        onClick={onPrintStatement}
                        type={BUTTON}
                        variant={TERTIARY}
                        label={CONTENT_STRINGS.DOWNLOAD_STATEMENT}
                        info={downloadStatementInfo}
                        arrowDirection={RIGHT}
                        buttonTestId={'advance-disbursements-download-statement'}
                    />
                    {isArag ? (
                        <Button
                            disabled={exportExcelDisabled}
                            onClick={onExportExcel}
                            type={BUTTON}
                            variant={TERTIARY}
                            label={DISBURSEMENTS_EXCEL_FILE_DOWNLOAD_BUTTON_LABEL}
                            info={DISBURSEMENTS_EXCEL_FILE_DOWNLOAD_BUTTON_INFO}
                            arrowDirection={RIGHT}
                            buttonTestId={'advance-disbursements-export-excel'}
                        />
                    ) : null}
                    {fetchingADDueForPayment ? <Loader variant={DOTS} dotSize={MEDIUM} /> : null}
                </div>
            </form>
            <div className='row'>
                <div className='col-sm-12'>
                    <Table
                        accordionItemHeaderFormatter={accordionItemHeaderFormatter}
                        onPageSizeChange={_changeTableViewOptions}
                        goAtPrevious={handlegoAtPrevious}
                        goAtNext={handlegoAtNext}
                        goAtStart={handlegoAtStart}
                        goAtEnd={handlegoAtEnd}
                        goAtPage={handlegoAtPage}
                        loading={fetchingAdvancedDisbursements}
                        responsive
                        striped
                        columns={tableColumns}
                        data={advancedDisbursements}
                        pageSize={parseInt(advancedDisbursementsList.viewAmount, 10)}
                        totalRecords={advancedDisbursementsList.totalRecords}
                        totalPages={advancedDisbursementsList.totalPaginationBoxes}
                        page={parseInt(advancedDisbursementsList.page)}
                        hasActions
                        actions={tableActions}
                        pageSizeOptions={UI_LIB_TABLE_VIEW_OPTIONS}
                        noResultsMessage={CONTENT_STRINGS.NO_RESULTS_FOUND}
                        tableWrapperTestId={'advance-disbursements-results-table'}
                    />
                </div>
            </div>
        </div>
    )
};

export default AdvancedDisbursementsList;